import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import SearchableSelect from "./SearchableSelect";
import { api } from "../lib/api";

/**
 * Création rapide de bénéficiaire (contact) depuis un autre formulaire.
 * Champs minimum : dénomination + catégorie + email + téléphone. Le département
 * est imposé par le formulaire parent (via `brandId`) et pré-rempli.
 *
 * Props :
 *  - open / onOpenChange  : contrôle standard de la Dialog
 *  - brandId              : département imposé (obligatoire, hérité du parent)
 *  - defaultCategory      : nom de catégorie souhaitée par défaut (ex. "Clientèle",
 *                           "Partenaire"). Résolue automatiquement depuis les
 *                           catégories disponibles ; l'utilisateur peut changer.
 *  - onCreated(newContact): callback avec le contact créé (id, denomination, email…)
 */
export default function QuickBeneficiaryDialog({
  open,
  onOpenChange,
  brandId,
  defaultCategory,
  onCreated,
}) {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    denomination: "",
    category_id: "",
    email: "",
    phone_country_code: "+33",
    phone_number: "",
  });
  const [saving, setSaving] = useState(false);

  // Charge la liste des catégories dès que la modale s'ouvre.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/beneficiary-categories");
        setCategories(data || []);
        // Résolution catégorie par défaut (par nom, insensible à la casse).
        const wanted = (defaultCategory || "").trim().toLowerCase();
        const match = wanted
          ? (data || []).find((c) => (c.name || "").toLowerCase() === wanted)
          : null;
        setForm((p) => ({
          ...p,
          category_id: match?.id || p.category_id || (data?.[0]?.id ?? ""),
        }));
      } catch {
        toast.error("Impossible de charger les catégories");
      }
    })();
  }, [open, defaultCategory]);

  // Réinitialise le formulaire à chaque fermeture.
  useEffect(() => {
    if (!open) {
      setForm({
        denomination: "",
        category_id: "",
        email: "",
        phone_country_code: "+33",
        phone_number: "",
      });
    }
  }, [open]);

  const submit = async () => {
    if (!form.denomination.trim()) {
      toast.error("La dénomination est obligatoire");
      return;
    }
    if (!form.category_id) {
      toast.error("Sélectionnez une catégorie");
      return;
    }
    if (!brandId) {
      toast.error("Département manquant");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        denomination: form.denomination.trim(),
        category_id: form.category_id,
        brand_ids: [brandId],
        email: form.email.trim() || null,
        phone_country_code: form.phone_number.trim() ? form.phone_country_code : null,
        phone_number: form.phone_number.trim() || null,
      };
      const { data } = await api.post("/beneficiaries", payload);
      toast.success("Contact créé");
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="quick-beneficiary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={16} className="text-emerald-600" />
            Nouveau contact (création rapide)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="label-uppercase mb-1.5 block">
              Dénomination<span className="text-red-500 ml-0.5">*</span>
            </Label>
            <Input
              value={form.denomination}
              onChange={(e) => setForm({ ...form, denomination: e.target.value })}
              placeholder="Nom / raison sociale"
              autoFocus
              data-testid="quick-beneficiary-denomination"
            />
          </div>

          <div>
            <Label className="label-uppercase mb-1.5 block">
              Catégorie<span className="text-red-500 ml-0.5">*</span>
            </Label>
            <SearchableSelect
              value={form.category_id}
              onChange={(v) => setForm({ ...form, category_id: v })}
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
              placeholder="Choisir une catégorie"
              searchPlaceholder="Rechercher…"
              data-testid="quick-beneficiary-category"
            />
          </div>

          <div>
            <Label className="label-uppercase mb-1.5 block">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="contact@example.com"
              data-testid="quick-beneficiary-email"
            />
          </div>

          <div className="grid grid-cols-[100px_1fr] gap-2">
            <div>
              <Label className="label-uppercase mb-1.5 block">Indicatif</Label>
              <Input
                value={form.phone_country_code}
                onChange={(e) => setForm({ ...form, phone_country_code: e.target.value })}
                placeholder="+33"
                data-testid="quick-beneficiary-country-code"
              />
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">Téléphone</Label>
              <Input
                value={form.phone_number}
                onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                placeholder="6 12 34 56 78"
                data-testid="quick-beneficiary-phone"
              />
            </div>
          </div>

          <p className="text-[11px] text-zinc-500 font-mono">
            Ce contact sera rattaché automatiquement au département courant. Vous pourrez
            compléter son profil (adresse, notes…) plus tard depuis la fiche contact.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="quick-beneficiary-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="quick-beneficiary-submit"
          >
            {saving ? "Création…" : "Créer le contact"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
